import React, { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Backdrop,
  Divider
} from '@mui/material';
import {
  Save,
  Edit
} from '@mui/icons-material';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useAuthGen2 } from '../hooks/useAuthGen2';
import { fetchAuthSession } from 'aws-amplify/auth';
import { buildApiEndpoint } from '../configuration/api';

interface UserStats {
  displayName?: string;
}

const Settings: React.FC = () => {
  const { user } = useAuthenticator((context) => [context.user]);
  const { isAuthenticated, loading: authLoading } = useAuthGen2();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [originalDisplayName, setOriginalDisplayName] = useState('');
  const [userEmail, setUserEmail] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Load user settings when auth is ready
  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (isAuthenticated) {
      loadUserSettings();
    } else {
      setLoading(false);
    }
  }, [authLoading, isAuthenticated]);

  // Load user settings from API
  const loadUserSettings = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      // Get email from ID token payload
      const email = session.tokens?.idToken?.payload?.email as string | undefined;
      setUserEmail(email || 'N/A');

      if (!idToken) {
        throw new Error('No authentication token available');
      }

      // Call the UserSettings API to get current display name
      const apiUrl = buildApiEndpoint('/user-settings', true);
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`API call failed: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.settings) {
        const currentDisplayName = data.settings.displayName || '';
        setDisplayName(currentDisplayName);
        setOriginalDisplayName(currentDisplayName);
      }
    } catch (error) {
      console.error('Error loading user settings:', error);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);
    setSaving(true);

    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No authentication token available');
      }

      const apiUrl = buildApiEndpoint('/display-name', true);
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          displayName: displayName.trim()
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to update display name');
      }

      setOriginalDisplayName(displayName.trim());
      setSuccess('Display name updated successfully!');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Error updating display name:', error);
      setError(error.message || 'Failed to update display name');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDisplayName(originalDisplayName);
    setError(null);
    setSuccess(null);
  };

  const hasChanges = displayName.trim() !== originalDisplayName;

  // Show loading state
  if (loading || authLoading) {
    return (
      <Backdrop
        sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
        open={loading || authLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  // Show auth required message if not authenticated
  if (!isAuthenticated) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, px: { xs: 0.25, sm: 0.2, md: 0.75 } }}>
        <Alert severity="info">
          Please sign in to access settings.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4, px: { xs: 0.25, sm: 0.2, md: 0.75 } }}>
      {/* Page Header */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Edit sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Settings
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Manage your account preferences and display name
        </Typography>
      </Paper>

      {/* Display Name Section */}
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Display Name
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Your display name appears on the leaderboard. Choose something memorable!
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <TextField
          fullWidth
          label="Display Name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          helperText="3-20 characters. Letters, numbers, spaces, hyphens, and underscores only."
          variant="outlined"
          sx={{ mb: 2 }}
          disabled={saving}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
            onClick={handleSave}
            disabled={!hasChanges || saving || !displayName.trim()}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={!hasChanges || saving}
          >
            Cancel
          </Button>
        </Box>
      </Paper>

      {/* Account Information */}
      <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Account Information
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Email:</strong> {userEmail || 'N/A'}
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default Settings;
